import findConversationWithStartIndex from 'owa-mail-store/lib/services/findConversationWithStartIndex';
import findItemWithStartIndex from 'owa-mail-store/lib/services/findItemWithStartIndex';
import isItemView from 'mini-mail-list-store/lib/utils/isItemView';
import MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import refreshSingleFolderCount from 'mini-mail-folder-store/lib/actions/data-loading/refreshSingleFolderCount';
import TableOperations from 'mini-mail-list-table-operations';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { action } from 'satcheljs/lib/legacy';
import { getFolderNameCustomData } from 'mini-mail-list-store/lib/selectors/getFolderNameCustomData';
import { getRowSortByProperty } from '../../utils/tables/getRowSortByProperty';
import { INITIAL_LOAD_ROW_COUNT } from './TableLoadingConstants';
import { logUsage } from 'owa-analytics';
import { MailListRowDataType } from 'mini-mail-list-store/lib/store/schema/MailListRowDataType';
import { mergeRowResponseFromTop } from 'mini-mail-list-response-processor';

export let onReloadTableSucceeded = action('onReloadTableSucceeded')(
    function onReloadTableSucceeded(
        tableView: TableView,
        reloadedRows: MailListRowDataType[],
        totalRowsInView: number
    ) {
        // Merge reloaded results from the top
        mergeRowResponseFromTop(reloadedRows, tableView, totalRowsInView);

        // When we reload, it generally indicates that we're out of sync with server, so we need to invalidate
        // remaining items we had after merging the top items
        for (let i = tableView.itemIds.length - 1; i >= reloadedRows.length; i--) {
            TableOperations.removeRow(tableView.itemIds[i], tableView);
        }
    }
);

/**
 * Called after reload table failed
 * @param responseCode the response code contained inside the reload response
 */
export let onReloadTableFailed = action('onReloadTableFailed')(function onReloadTableFailed(
    responseCode?: string
) {
    logUsage('TnS_ReloadError', [responseCode]);
});

/**
 * Reload the conversation table
 * @param tableView the conversation table view to be reloaded
 * @param rowsToRefresh the number of rows to refresh
 */
const reloadConversationTable = (tableView: TableView, rowsToRefresh: number) => {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;

    return findConversationWithStartIndex({
        baseFolderId: mailTableQuery.baseFolderId,
        startIndex: 0,
        rowsToLoad: rowsToRefresh,
        viewFilter: mailTableQuery.viewFilter,
        sortResults: getRowSortByProperty(mailTableQuery),
        focusedViewFilter: mailTableQuery.focusedViewFilter,
        findConversationShapeName: mailTableQuery.requestShapeName,
        shouldTryToUseSessionDataResponse: mailTableQuery.shouldTryToUseSessionDataResponse,
    })
        .then(responseMessage => {
            if (responseMessage.ResponseClass === 'Success') {
                onReloadTableSucceeded(
                    tableView,
                    responseMessage.Conversations,
                    responseMessage.TotalConversationsInView
                );
            } else {
                onReloadTableFailed(responseMessage.ResponseCode);
            }
        })
        .catch(() => {
            onReloadTableFailed();
        });
};

/**
 * Reload the item table
 * @param tableView the item table view to be reloaded
 * @param rowsToRefresh number of rows to refresh
 */
const reloadItemTable = (tableView: TableView, rowsToRefresh: number) => {
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;

    return findItemWithStartIndex({
        folderId: tableView?.tableQuery?.folderId,
        rowsToLoad: rowsToRefresh,
        viewFilter: mailTableQuery.viewFilter,
        focusedViewFilter: mailTableQuery.focusedViewFilter,
        shouldTryToUseSessionDataResponse: mailTableQuery.shouldTryToUseSessionDataResponse,
        sortOrder: getRowSortByProperty(mailTableQuery, false /* isConversation */),
    })
        .then(responseMessage => {
            if (responseMessage.ResponseClass === 'Success') {
                onReloadTableSucceeded(
                    tableView,
                    responseMessage.RootFolder.Items,
                    responseMessage.RootFolder.TotalItemsInView
                );
            } else {
                onReloadTableFailed(responseMessage.ResponseCode);
            }
        })
        .catch(() => {
            onReloadTableFailed();
        });
};

export default action('reloadTable')(function reloadTable(tableView: TableView) {
    logUsage('ReloadTable', getFolderNameCustomData(tableView?.tableQuery?.folderId));

    // Reload all items that we have, otherwise rows may be in an inconsistent state
    // TODO: 11814: Optimize reload performance since we currently reload all items that we have
    // NOTE: this no. should be greater than 0.
    const rowsToRefresh = Math.max(tableView.currentLoadedIndex || 0, INITIAL_LOAD_ROW_COUNT);
    let reloadPromise: Promise<void>;

    if (isItemView(tableView)) {
        reloadPromise = reloadItemTable(tableView, rowsToRefresh);
    } else {
        reloadPromise = reloadConversationTable(tableView, rowsToRefresh);
    }

    // Also refresh counts for folder
    refreshSingleFolderCount(tableView.tableQuery.folderId);

    return reloadPromise;
});
